import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface DependencyState {
  src: string;
  loaded: boolean;
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class AssetsManagerService {
  private dependencies: {} = {};

  constructor(private httpClient: HttpClient) {}

  getFile(url: string): Observable<any> {
    return this.httpClient.get(url, { observe: 'response' }).pipe(
      map((response) => {
        if (response.status < 400) {
          return response.body;
        } else {
          console.error('Error loading file: ' + url);
          return undefined;
        }
      }),
      catchError((err: Response) => {
        if (err.status === 404) {
          return throwError(err);
        } else {
          console.error('Invalid file: ' + url);
          return of(undefined);
        }
      })
    );
  }

  loadDependencies(sources: string[]): Promise<DependencyState[]> {
    sources.forEach((src: string) => {
      if (!this.dependencies[src]) {
        this.dependencies[src] = { src: src, loaded: false };
      }
    });
    let promises: Promise<DependencyState>[] = [];
    sources.forEach((src) => promises.push(this.loadDependency(src)));
    return Promise.all(promises);
  }

  private loadDependency(src: string): Promise<DependencyState> {
    return new Promise((resolve, reject) => {
      //resolve if already loaded
      if (this.dependencies[src].loaded) {
        resolve({ src: src, loaded: true, status: 'Already Loaded' });
      } else {
        //load script
        let element = null;
        let path = src.split('?')[0];
        if (path.endsWith('.js')) {
          element = document.createElement('script');
          element.type = 'text/javascript';
          element.src = src;
          element.innerText = '&nbsp;';
        } else if (path.endsWith('.css')) {
          element = document.createElement('link');
          element.type = 'text/css';
          element.rel = 'stylesheet';
          element.href = src;
        }
        if (element) {
          if (element.readyState) {
            //IE
            element.onreadystatechange = () => {
              if (element.readyState === 'loaded' || element.readyState === 'complete') {
                element.onreadystatechange = null;
                this.dependencies[src].loaded = true;
                resolve({ src: src, loaded: true, status: 'Loaded' });
              }
            };
          } else {
            //Others
            element.onload = () => {
              this.dependencies[src].loaded = true;
              resolve({ src: src, loaded: true, status: 'Loaded' });
            };
          }
          element.onerror = (error: any) => resolve({ src: src, loaded: false, status: 'Error' });
          document.head.appendChild(element);
        } else {
          resolve({ src: src, loaded: false, status: 'Error' });
        }
      }
    });
  }
}
